import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { Toaster } from './ui/sonner';

const LegalPage = ({ type, lang }) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const targetLang = lang || 'en';
    if (i18n.language !== targetLang) i18n.changeLanguage(targetLang);
    document.documentElement.lang = targetLang;
    document.documentElement.dir = targetLang === 'ar' ? 'rtl' : 'ltr';
    window.scrollTo(0, 0);
  }, [lang, i18n]);

  const isRTL = i18n.language === 'ar';
  const sections = t(`legal.${type}.sections`, { returnObjects: true });
  const homePath = lang && lang !== 'en' ? `/${lang}` : '/';

  return (
    <div className={`min-h-screen bg-black ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>{`${t(`legal.${type}.title`)} | Oualid BEN NAID`}</title>
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Header />
      <main className="max-w-3xl mx-auto px-6 pt-32 pb-24">
        <Link
          to={homePath}
          data-testid="legal-back-link"
          className="inline-flex items-center gap-2 text-[#00FFD1] hover:text-white transition-colors duration-300 text-sm mb-10"
        >
          <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
          <span>{t('legal.back')}</span>
        </Link>

        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3" data-testid="legal-title">
          {t(`legal.${type}.title`)}
        </h1>
        <p className="text-white/40 text-sm mb-12">{t(`legal.${type}.updated`)}</p>

        <div className="space-y-10">
          {Array.isArray(sections) &&
            sections.map((s, i) => (
              <section key={s.heading || i} data-testid={`legal-section-${i}`}>
                <h2 className="text-lg md:text-xl font-semibold text-white mb-3">{s.heading}</h2>
                {String(s.body)
                  .split('\n\n')
                  .map((p, j) => (
                    <p key={j} className="text-white/60 text-sm leading-relaxed mb-3">
                      {p}
                    </p>
                  ))}
              </section>
            ))}
        </div>

        {type === 'privacy' && (
          <div
            data-testid="privacy-cookie-callout"
            className="mt-12 p-6 border border-[#00FFD1]/20 bg-[#00FFD1]/[0.03] flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <p className="text-white/70 text-sm flex-1">{t('legal.cookiePrompt')}</p>
            <button
              onClick={() => window.dispatchEvent(new Event('open-cookie-consent'))}
              data-testid="privacy-cookie-settings-button"
              className="px-5 py-2.5 text-sm font-medium text-black bg-[#00FFD1] hover:bg-[#00e6bd] transition-colors duration-300 whitespace-nowrap"
            >
              {t('legal.cookieButton')}
            </button>
          </div>
        )}
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </div>
  );
};

export default LegalPage;
